import {observer} from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';

import events from 'events.js';
import autobind from 'common/decorators/autobind.js';
import AutocompleteSuggestions from 'core/components/AutocompleteSuggestions.js';
import Button from 'core/components/Button.js';
import Icon from 'core/components/Icon.js';

export default
@observer
class Autocomplete extends React.Component {
    static propTypes = {
        autoFocus: PropTypes.bool,
        // Both passed straight to AutocompleteSuggestions; see its propTypes.
        emptyListClassName: PropTypes.string,
        hasLabelAnimation: PropTypes.bool,
        // Bold the matched run in each suggestion. The animated-label branch
        // has always done this; the plain branch needs it for the directory
        // widget, whose Knockout dropdown highlighted matches.
        highlightResults: PropTypes.bool,
        inputErrorRef: PropTypes.object,
        // Class for the label text itself, not the wrapping <label>. The
        // directory widgets need `hs-label` here: the per-fund stylesheets
        // target `.hs-field .hs-label`, and those rules include
        // `text-transform: uppercase`, which would reach the input's
        // placeholder if it sat on the wrapper.
        labelClassName: PropTypes.string,
        labelText: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
            .isRequired,
        // Passed straight to AutocompleteSuggestions; see its propTypes for
        // why the directory widget overrides the dropdown's markup.
        listClassName: PropTypes.string,
        // Lock the field once a result is chosen: the input goes `readonly`
        // and a Clear button appears beside it, as the Knockout widget's
        // `afterSelect`/`afterReset` pair did. style.less hangs the
        // chosen-field styling off `input[readonly]`, and widget-th.css,
        // widget-rthf.css and widget-hif.css each add their own border
        // colour, so the attribute is a styling contract and not only a
        // behaviour. Off by default - only the directory widgets want it.
        // Implemented by the plain branch; the animated-label branch already
        // renders its own reset control whenever there is a value.
        lockOnSelect: PropTypes.bool,
        maxLength: PropTypes.number,
        // The input's form field name. The directory widgets need it: their
        // form posts to the directory index, whose DirectorySearchForm reads
        // `specialty` as a *name* string and has no `specialty_id` field at
        // all - so without this the Search button submits nothing usable.
        // Unset elsewhere, which renders no attribute.
        name: PropTypes.string,
        onFocus: PropTypes.func,
        onSubmit: PropTypes.func,
        placeholder: PropTypes.string.isRequired,
        renderEmpty: PropTypes.func,
        // The "Please select a result from the dropdown" live region. On by
        // default; the directory widget turns it off because the Knockout
        // original has no equivalent, `.notice` is unstyled on the widget
        // page, and the widget already signals the requirement with the red
        // asterisk and by hiding the Search button.
        showSelectionNotice: PropTypes.bool,
        // Passed straight to AutocompleteSuggestions; see its propTypes.
        renderIcon: PropTypes.func,
        store: PropTypes.object.isRequired,
        uniqueId: PropTypes.string.isRequired,
        wrapperClassName: PropTypes.string,
    };

    static defaultProps = {
        autoFocus: false,
        lockOnSelect: false,
        maxLength: null,
        showSelectionNotice: true,
    };

    constructor(props) {
        super(props);
        this.inputRef = React.createRef();
        this.isFocused = false;
    }

    componentDidMount() {
        const {isStateStore, selected} = this.props.store;
        // show results immediately for state search
        if (isStateStore && !selected?.id) {
            // make sure call comes after click event handler
            setTimeout(() => {
                this.props.store.updateStore({
                    showResults: true,
                });
            }, 0);
        }
        events.listen(document.body, 'click', this.handleClick);
    }

    componentWillUnmount() {
        events.unlisten(document.body, 'click', this.handleClick);
    }

    @autobind
    handleClick(event) {
        const {store} = this.props;
        let showError = store.inputError.showError;
        let errorMessageWasShown = store.inputError.errorMessageWasShown;
        if (
            this.inputRef?.current &&
            !this.inputRef.current.contains(event.target)
        ) {
            this.hideResults();
            if (
                !store?.selected.id &&
                !store.inputError.errorMessageWasShown
            ) {
                errorMessageWasShown = true;
                showError =
                    event.target.getAttribute('aria-autocomplete') !== 'list';
            } else {
                showError = false;
            }
        } else {
            showError = false;
            errorMessageWasShown = false;
        }

        store.updateStore({
            inputError: {
                showError,
                errorMessageWasShown,
            },
        });
    }

    @autobind
    handleChange(event) {
        const {value} = event.target;
        const {isStateStore} = this.props.store;
        this.props.store.updateStore({
            value,
            selected: {},
            showResults: !!value.length || isStateStore,
            highlightedResult: 0,
            inputError: {
                showError: false,
                errorMessageWasShown: false,
            },
        });
    }

    @autobind
    handleKeyDown(event) {
        const {store} = this.props;
        const i = store.highlightedResultIndex;
        switch (event.key) {
            case 'Esc':
            case 'Escape':
                this.hideResults();
                break;
            case 'ArrowDown':
                event.preventDefault();
                store.updateStore({
                    highlightedResult: i + 1,
                });
                break;
            case 'ArrowUp':
                event.preventDefault();
                store.updateStore({
                    highlightedResult: i - 1,
                });
                break;
            case 'Enter':
                if (!store.selected.id) {
                    event.preventDefault();
                } else {
                    this.handleSubmit(event);
                }
            // eslint-disable-next-line no-fallthrough
            case 'Tab':
                if (store.suggestions.length) {
                    store.updateStore({
                        value: store.suggestions[i].name,
                        selected: store.suggestions[i],
                        showResults: false,
                    });
                }
        }
    }

    @autobind
    hideResults() {
        if (this.props.store.showResults) {
            this.props.store.updateStore({
                showResults: false,
            });
        }
    }

    @autobind
    selectText(event) {
        if (!this.isFocused) {
            const input = event.target;
            input.setSelectionRange(0, input.value.length);
            this.isFocused = true;
            this.showResults();
        }
    }

    @autobind
    handleBlur() {
        const {store} = this.props;
        this.isFocused = false;
        if (!store.selected?.id) {
            store.updateStore({
                inputError: {
                    showError: true,
                },
            });
        }
    }

    @autobind
    handleFocus() {
        const {store} = this.props;
        this.showResults();
        store.updateStore({
            inputError: {
                showError: false,
            },
        });
    }

    @autobind
    showResults() {
        const {onFocus} = this.props;
        if (onFocus) {
            onFocus();
        }
        if (!this.props.store.showResults && this.props.store.results.length) {
            this.props.store.updateStore({
                showResults: true,
                highlightedResult: 0,
            });
        }
    }

    @autobind
    resetSearch() {
        const {inputErrorRef} = this.props;
        this.props.store.updateStore({
            value: '',
            selected: {},
            showResults: false,
            highlightedResult: 0,
            inputError: {
                showError: false,
                errorMessageWasShown: false,
            },
        });
        if (inputErrorRef?.current) {
            inputErrorRef.current.focus();
        }
    }

    @autobind
    handleSubmit(e) {
        e.preventDefault();
        const {onSubmit} = this.props;
        if (onSubmit) {
            onSubmit();
        }
    }

    renderErrorMessage() {
        if (!this.props.showSelectionNotice) {
            return null;
        }
        // prevent error from rendering if search input is the active element
        const inputHasFocus =
            this.inputRef.current?.childNodes[0] === document.activeElement;
        if (
            this.props.store.inputError.showError &&
            !this.props.store.selected?.id &&
            !inputHasFocus
        ) {
            return (
                <span className="notice" role="alert">
                    {'Please select a result from the dropdown'}
                </span>
            );
        }
    }

    get searchStringLength() {
        const {value, selected} = this.props.store;
        const searchString = selected.name || value;
        return searchString?.length || 0;
    }

    render() {
        const {
            emptyListClassName,
            handleSelectResult,
            highlightResults,
            labelClassName,
            labelText,
            listClassName,
            lockOnSelect,
            maxLength,
            name,
            placeholder,
            renderEmpty,
            renderIcon,
            store,
            uniqueId,
            wrapperClassName,
            autoFocus,
            onSubmit,
        } = this.props;
        const {
            highlightedResultIndex,
            value,
            selected,
            showResults,
            suggestions,
        } = store;
        const isLocked = lockOnSelect && !!selected?.id;
        return (
            <>
                {this.props.hasLabelAnimation ? (
                    <fieldset
                        aria-expanded={showResults}
                        aria-haspopup="listbox"
                        ref={this.inputRef}
                        role="combobox"
                    >
                        <input
                            aria-activedescendant={
                                showResults && !!suggestions.length
                                    ? suggestions[highlightedResultIndex]?.id
                                    : null
                            }
                            aria-autocomplete="list"
                            aria-controls={
                                showResults ? `${uniqueId}-list` : null
                            }
                            aria-multiline={false}
                            autoComplete="off"
                            autoFocus={autoFocus}
                            className={
                                this.searchStringLength
                                    ? 'placeholder-hidden'
                                    : ''
                            }
                            id={`${uniqueId}-input`}
                            maxLength={maxLength}
                            name={name}
                            onBlur={this.handleBlur}
                            onChange={this.handleChange}
                            onClick={this.selectText}
                            onFocus={(e) => {
                                this.handleFocus();
                                e.target.select();
                            }}
                            onKeyDown={this.handleKeyDown}
                            placeholder=" "
                            ref={this.props.inputErrorRef}
                            type="text"
                            value={selected?.name || value}
                            data-testid="autocomplete-input"
                        />
                        <label htmlFor={`${uniqueId}-input`}>
                            {labelText}
                        </label>
                        {onSubmit && (
                            <Button
                                action={this.handleSubmit}
                                text={'Submit'}
                                type="submit"
                            />
                        )}
                        {(!!selected?.id || value) && (
                            <Button
                                action={this.resetSearch}
                                customClass="reset-field"
                                text={<Icon name="close" />}
                                type="reset"
                            />
                        )}
                        {this.renderErrorMessage()}
                        <AutocompleteSuggestions
                            emptyListClassName={emptyListClassName}
                            handleSelectResult={handleSelectResult}
                            hideResults={this.hideResults}
                            highlightResults={true}
                            listClassName={listClassName}
                            renderEmpty={renderEmpty}
                            renderIcon={renderIcon}
                            store={store}
                            uniqueId={uniqueId}
                            wrapperClassName={wrapperClassName}
                        />
                    </fieldset>
                ) : (
                    <div
                        aria-expanded={showResults}
                        aria-haspopup="listbox"
                        ref={this.inputRef}
                        role="combobox"
                    >
                        <label>
                            <span className={labelClassName}>{labelText}</span>
                            <input
                                aria-activedescendant={
                                    showResults && !!suggestions.length
                                        ? suggestions[highlightedResultIndex]
                                              ?.id
                                        : null
                                }
                                aria-autocomplete="list"
                                aria-controls={
                                    showResults ? `${uniqueId}-list` : null
                                }
                                aria-multiline={false}
                                autoComplete="off"
                                autoFocus={autoFocus}
                                id={`${uniqueId}-input`}
                                maxLength={maxLength}
                                name={name}
                                onBlur={this.handleBlur}
                                onChange={this.handleChange}
                                onClick={this.selectText}
                                onFocus={this.handleFocus}
                                onKeyDown={this.handleKeyDown}
                                placeholder={placeholder}
                                readOnly={isLocked}
                                ref={this.props.inputErrorRef}
                                type="text"
                                value={selected?.name || value}
                                data-testid="autocomplete-input"
                            />
                        </label>
                        {isLocked && (
                            // The lock has to be escapable. Knockout revealed
                            // this button through
                            // `input[readonly] ~ [type='reset']`, so shipping
                            // `readOnly` without it would leave a member
                            // unable to change a choice they had already
                            // made. `type="reset"` is what the stylesheet
                            // matches on, but a native form reset would also
                            // wipe the sibling field in the same form, so the
                            // default is suppressed and resetSearch clears
                            // this field alone.
                            <Button
                                action={this.resetSearch}
                                isPreventDefault={true}
                                text={'Clear'}
                                type="reset"
                            />
                        )}
                        {this.renderErrorMessage()}
                        <AutocompleteSuggestions
                            emptyListClassName={emptyListClassName}
                            handleSelectResult={handleSelectResult}
                            hideResults={this.hideResults}
                            highlightResults={highlightResults}
                            listClassName={listClassName}
                            renderEmpty={renderEmpty}
                            renderIcon={renderIcon}
                            store={store}
                            uniqueId={uniqueId}
                            wrapperClassName={wrapperClassName}
                        />
                    </div>
                )}
            </>
        );
    }
}
